import { convertQuantityBetweenUnits, convertQuantityToInventoryUnit } from "./unitConversion";
import { hasConfiguredReorderPoint } from "./stockLevels";
import { parseLocaleNumber } from "./decimal";

function toFiniteNumber(value, fallback = 0) {
  const number = parseLocaleNumber(value);
  return Number.isFinite(number) ? number : fallback;
}

function getRecipeServings(recipe) {
  const servings = toFiniteNumber(recipe?.servings, 1);
  return servings > 0 ? servings : 1;
}

function findInventoryItem(inventory, ingredient) {
  return inventory.find((item) =>
    item.id === ingredient.inventory_item_id || item.name === ingredient.inventory_item_name
  );
}

function normalizeName(value) {
  return `${value ?? ""}`.trim().toLowerCase();
}

function buildRecipeMaps(recipes) {
  const byId = new Map();
  const byName = new Map();
  const byOutputInventoryItemId = new Map();

  recipes.forEach((recipe) => {
    if (recipe.id !== undefined && recipe.id !== null) {
      byId.set(Number(recipe.id), recipe);
    }
    const name = normalizeName(recipe.name);
    if (name) byName.set(name, recipe);
    if (recipe.output_inventory_item_id) {
      byOutputInventoryItemId.set(Number(recipe.output_inventory_item_id), recipe);
    }
  });

  return { byId, byName, byOutputInventoryItemId };
}

function inferRecipeOutputQuantity(recipe, outputUnit, inventoryById) {
  const ingredients = recipe?.ingredients || [];
  const inferred = ingredients.reduce((sum, ingredient) => {
    const ingredientItem = inventoryById.get(Number(ingredient.inventory_item_id));
    const sourceUnit = ingredient.unit || ingredientItem?.unit || "";
    const converted = convertQuantityBetweenUnits(
      toFiniteNumber(ingredient.quantity_needed),
      sourceUnit,
      outputUnit,
    );
    return converted > 0 ? sum + converted : sum;
  }, 0);

  return inferred > 0 ? inferred : getRecipeServings(recipe);
}

export function calculateProjectedStockAlerts({ forecasts = [], recipes = [], inventory = [] }) {
  if (!forecasts.length || !recipes.length || !inventory.length) return [];

  const inventoryById = new Map(inventory.map((item) => [Number(item.id), item]));
  const recipeMaps = buildRecipeMaps(recipes);
  const consumptionByItemId = {};
  const dailyConsumptionByDate = new Map();

  function addConsumption(inventoryItem, quantity, unit, date) {
    const convertedQuantity = convertQuantityToInventoryUnit(
      quantity,
      unit,
      inventoryItem.unit,
    );
    if (convertedQuantity <= 0) return;
    consumptionByItemId[inventoryItem.id] = (
      consumptionByItemId[inventoryItem.id] || 0
    ) + convertedQuantity;

    if (date) {
      const dayConsumption = dailyConsumptionByDate.get(date) || {};
      dayConsumption[inventoryItem.id] = (
        dayConsumption[inventoryItem.id] || 0
      ) + convertedQuantity;
      dailyConsumptionByDate.set(date, dayConsumption);
    }
  }

  function consumeIngredient(ingredient, quantityNeeded, unit, stack, date) {
    const inventoryItem = findInventoryItem(inventory, ingredient);
    if (!inventoryItem || quantityNeeded <= 0) return;

    addConsumption(inventoryItem, quantityNeeded, unit || ingredient.unit, date);

    const sourceRecipe = recipeMaps.byOutputInventoryItemId.get(Number(inventoryItem.id));
    if (!sourceRecipe || stack.has(sourceRecipe.id)) return;

    const outputUnit = sourceRecipe.output_inventory_unit || inventoryItem.unit;
    const requiredOutputQuantity = convertQuantityBetweenUnits(
      quantityNeeded,
      unit || ingredient.unit || inventoryItem.unit,
      outputUnit,
    );
    const outputQuantity = inferRecipeOutputQuantity(sourceRecipe, outputUnit, inventoryById);
    if (requiredOutputQuantity <= 0 || outputQuantity <= 0) return;

    const multiplier = requiredOutputQuantity / outputQuantity;
    consumeRecipeBatch(sourceRecipe, multiplier, new Set([...stack, sourceRecipe.id]), date);
  }

  function consumeRecipeBatch(recipe, multiplier, stack, date) {
    (recipe.ingredients || []).forEach((ingredient) => {
      const quantityNeeded = toFiniteNumber(ingredient.quantity_needed) * multiplier;
      consumeIngredient(ingredient, quantityNeeded, ingredient.unit, stack, date);
    });
  }

  function consumeRecipeServings(recipe, servingsToProduce, date) {
    const servings = getRecipeServings(recipe);
    const multiplier = servingsToProduce / servings;
    consumeRecipeBatch(recipe, multiplier, new Set([recipe.id]), date);
  }

  forecasts.forEach((day) => {
    if (day.is_closed || !Array.isArray(day.items)) return;

    day.items.forEach((forecastItem) => {
      const quantity = toFiniteNumber(forecastItem.quantity);
      if (quantity <= 0) return;

      const recipe = recipeMaps.byId.get(Number(forecastItem.recipe_id))
        || recipeMaps.byName.get(normalizeName(forecastItem.name));
      if (!recipe) return;

      consumeRecipeServings(recipe, quantity, day.date);
    });
  });

  function calculateFirstReorderBreach(item) {
    const currentQuantity = toFiniteNumber(item.current_quantity);
    const reorderPoint = toFiniteNumber(item.reorder_point);
    let cumulativeConsumption = 0;

    for (const date of [...dailyConsumptionByDate.keys()].sort()) {
      cumulativeConsumption += dailyConsumptionByDate.get(date)?.[item.id] || 0;
      const stockAfterDay = currentQuantity - cumulativeConsumption;
      if (stockAfterDay < reorderPoint) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const targetDate = new Date(`${date}T00:00:00`);
        const daysUntil = Number.isNaN(targetDate.getTime())
          ? null
          : Math.max(0, Math.round((targetDate.getTime() - today.getTime()) / 86400000));
        return {
          reorder_breach_date: date,
          days_until_reorder_breach: daysUntil,
          projected_consumption_at_reorder_breach: cumulativeConsumption,
          stock_at_reorder_breach: stockAfterDay,
        };
      }
    }

    return {
      reorder_breach_date: null,
      days_until_reorder_breach: null,
      projected_consumption_at_reorder_breach: null,
      stock_at_reorder_breach: null,
    };
  }

  return inventory
    .filter((item) => hasConfiguredReorderPoint(item) && consumptionByItemId[item.id] > 0)
    .map((item) => ({
      ...item,
      projected_consumption: consumptionByItemId[item.id],
      stock_after: toFiniteNumber(item.current_quantity) - consumptionByItemId[item.id],
      ...calculateFirstReorderBreach(item),
    }))
    .filter((item) => item.stock_after < toFiniteNumber(item.reorder_point))
    .sort((a, b) => {
      const aDays = a.days_until_reorder_breach ?? Number.MAX_SAFE_INTEGER;
      const bDays = b.days_until_reorder_breach ?? Number.MAX_SAFE_INTEGER;
      if (aDays !== bDays) return aDays - bDays;
      return a.stock_after - b.stock_after;
    });
}
